/**
 * Hook for server-side NCO (New Client Onboarding) grid data
 *
 * Fetches data from the in-memory search index with pagination, sorting, and filtering.
 * Designed to work with MUI Data Grid Premium server-side mode.
 *
 * Uses React Query for cache management and invalidation.
 * Invalidate with: queryClient.invalidateQueries({ queryKey: ['search', 'nco'] })
 */

import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-premium'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

const API_BASE = import.meta.env.VITE_API_URL || 'https://grobotic.app:2096'

/** Query key for NCO search - use for invalidation */
export const NCO_SEARCH_KEY = ['search', 'nco'] as const

interface SearchParams {
  page: number
  pageSize: number
  sortModel?: GridSortModel
  filterModel?: GridFilterModel
}

interface SearchResult {
  rows: any[]
  totalCount: number
  page: number
  pageSize: number
  aggregations?: {
    total: number
    grossProfit: number
    mediaSpend: number
    implementationFee: number
  }
}

interface UseServerSideNcoOptions {
  enabled?: boolean
  pageSize?: number
  initialSortModel?: GridSortModel
  initialFilterModel?: GridFilterModel
}

interface UseServerSideNcoReturn {
  // Data
  rows: any[]
  totalRowCount: number
  aggregations?: SearchResult['aggregations']

  // Pagination
  page: number
  pageSize: number
  onPageChange: (newPage: number) => void
  onPageSizeChange: (newPageSize: number) => void

  // Sorting
  sortModel: GridSortModel
  onSortModelChange: (newSortModel: GridSortModel) => void

  // Filtering
  filterModel: GridFilterModel
  onFilterModelChange: (newFilterModel: GridFilterModel) => void

  // Loading state
  isLoading: boolean
  error: string | null

  // Refresh
  refresh: () => Promise<void>

  // Server-side mode props for DataGridPremium
  serverSideProps: {
    paginationMode: 'server'
    sortingMode: 'server'
    filterMode: 'server'
    rowCount: number
    paginationModel: { page: number; pageSize: number }
    onPaginationModelChange: (model: { page: number; pageSize: number }) => void
    loading: boolean
  }
}

/**
 * Fetch function for the NCO search API
 */
const fetchNcoSearch = async (params: SearchParams): Promise<SearchResult> => {
  const response = await fetch(`${API_BASE}/search/nco`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    throw new Error(`NCO search failed: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Hook for fetching NCO deals with server-side pagination, sorting, and filtering
 */
export const useServerSideNco = (options: UseServerSideNcoOptions = {}): UseServerSideNcoReturn => {
  const {
    enabled = true,
    pageSize: initialPageSize = 50,
    initialSortModel = [
      { field: 'dealStatus', sort: 'desc' },
      { field: 'organizationName', sort: 'asc' },
    ],
    initialFilterModel = { items: [], quickFilterValues: [] },
  } = options

  const queryClient = useQueryClient()

  // Controlled state for grid parameters
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel)
  const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel)

  // Debounced filter model for the query
  const [debouncedFilterModel, setDebouncedFilterModel] = useState<GridFilterModel>(initialFilterModel)
  const filterDebounceRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Debounce filter changes
  useEffect(() => {
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current)
    }

    filterDebounceRef.current = setTimeout(() => {
      setDebouncedFilterModel(filterModel)
      setPage(0)
    }, 300)

    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current)
      }
    }
  }, [filterModel])

  // Build query key with all search parameters
  const queryKey = [...NCO_SEARCH_KEY, { page, pageSize, sortModel, filterModel: debouncedFilterModel }] as const

  // Use React Query for data fetching
  const {
    data,
    isLoading,
    isFetching,
    error: queryError,
  } = useQuery({
    queryKey,
    queryFn: () =>
      fetchNcoSearch({
        page,
        pageSize,
        sortModel,
        filterModel: debouncedFilterModel,
      }),
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    placeholderData: previousData => previousData,
  })

  // Extract data from query result
  const rows = data?.rows ?? []
  const totalRowCount = data?.totalCount ?? 0
  const aggregations = data?.aggregations

  const onPageChange = useCallback((newPage: number) => {
    setPage(newPage)
  }, [])

  const onPageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(0)
  }, [])

  const onSortModelChange = useCallback((newSortModel: GridSortModel) => {
    setSortModel(newSortModel)
    setPage(0)
  }, [])

  const onFilterModelChange = useCallback((newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel)
  }, [])

  const onPaginationModelChange = useCallback((model: { page: number; pageSize: number }) => {
    setPage(model.page)
    setPageSize(model.pageSize)
  }, [])

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: NCO_SEARCH_KEY })
  }, [queryClient])

  // Props to spread onto DataGridPremium for server-side mode
  const serverSideProps = {
    paginationMode: 'server' as const,
    sortingMode: 'server' as const,
    filterMode: 'server' as const,
    rowCount: totalRowCount,
    paginationModel: { page, pageSize },
    onPaginationModelChange,
    loading: isLoading || isFetching,
  }

  return {
    rows,
    totalRowCount,
    aggregations,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
    isLoading: isLoading || isFetching,
    error: queryError?.message ?? null,
    refresh,
    serverSideProps,
  }
}

/**
 * Get NCO-specific statistics
 */
export const getNcoStats = async (): Promise<{
  totalDeals: number
  onboardingEligible: number
  lastSync: string | null
}> => {
  try {
    const response = await fetch(`${API_BASE}/search/nco/stats`)
    return response.json()
  } catch (err) {
    return { totalDeals: 0, onboardingEligible: 0, lastSync: null }
  }
}

export default useServerSideNco
